import { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { useQuery } from 'react-query'
import { challengesAPI, submissionsAPI } from '../../services/api'
import { useAuth } from '../../contexts/AuthContext'
import { 
  ArrowLeftIcon,
  DocumentArrowUpIcon,
  InformationCircleIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline'
import LoadingSpinner from '../../components/ui/LoadingSpinner'
import { format } from 'date-fns'

export default function SubmitSolutionPage() {
  const { challengeId } = useParams()
  const { user } = useAuth()
  const navigate = useNavigate()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [dragActive, setDragActive] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
    setValue,
    watch,
  } = useForm()

  const watchedFiles = watch('files')

  // Challenge details
  const { data: challengeData, isLoading: challengeLoading, error } = useQuery(
    ['challenge', challengeId],
    () => challengesAPI.getById(challengeId),
    {
      select: (response) => response.data.challenge
    }
  )

  // Check if user already submitted
  const { data: existingSubmission } = useQuery(
    ['user-submission', challengeId, user?.id],
    () => submissionsAPI.getByUserAndChallenge(user.id, challengeId),
    {
      enabled: !!user?.id && !!challengeId,
      select: (response) => response.data[0]
    }
  )

  useEffect(() => {
    if (existingSubmission) {
      navigate(`/submissions`)
    }
  }, [existingSubmission, navigate])

  const onSubmit = async (data) => {
    setIsSubmitting(true)
    try {
      const formData = {
        challengeId: challengeId,
        title: data.title,
        description: data.description,
        files: data.files
      }

      const result = await submissionsAPI.create(formData)
      
      if (result.data) {
        navigate('/submissions', { 
          state: { message: 'Solution submitted successfully!' }
        })
      }
    } catch (error) {
      console.error('Submission error:', error)
      setError('root', { 
        message: error.response?.data?.message || 'Failed to submit solution. Please try again.' 
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDrag = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true)
    } else if (e.type === 'dragleave') {
      setDragActive(false)
    }
  }

  const handleDrop = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setValue('files', e.dataTransfer.files)
    }
  }

  const handleFileChange = (e) => {
    if (e.target.files) {
      setValue('files', e.target.files)
    }
  }

  if (challengeLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (error || !challengeData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Challenge not found</h2>
          <p className="text-gray-600 mb-4">The challenge you're trying to submit to doesn't exist.</p>
          <Link to="/challenges" className="btn-primary">
            Back to Challenges
          </Link>
        </div>
      </div>
    )
  }

  if (challengeData.status !== 'active') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-yellow-400 mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Challenge Not Active</h2>
          <p className="text-gray-600 mb-4">
            This challenge is not currently accepting submissions.
          </p>
          <Link to="/challenges" className="btn-primary">
            Browse Active Challenges
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
          <div className="flex items-center mb-4">
            <button
              onClick={() => navigate(-1)}
              className="mr-4 p-2 text-gray-400 hover:text-gray-600"
            >
              <ArrowLeftIcon className="h-5 w-5" />
            </button>
            <div>
              <h1 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
                Submit Solution
              </h1>
              <p className="mt-1 text-sm text-gray-500">
                Submit your solution for: <span className="font-medium">{challengeData.title}</span>
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Submission Form */}
          <div className="lg:col-span-2">
            <div className="bg-white shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-6">Solution Details</h3>
                
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                  {/* Title */}
                  <div>
                    <label htmlFor="title" className="block text-sm font-medium text-gray-700">
                      Solution Title *
                    </label>
                    <input
                      {...register('title', {
                        required: 'Solution title is required',
                        minLength: {
                          value: 5,
                          message: 'Title must be at least 5 characters',
                        },
                        maxLength: {
                          value: 200,
                          message: 'Title must be less than 200 characters',
                        },
                      })}
                      type="text"
                      className={`mt-1 block w-full px-3 py-2 border ${
                        errors.title ? 'border-red-300' : 'border-gray-300'
                      } rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm`}
                      placeholder="Enter a descriptive title for your solution"
                    />
                    {errors.title && (
                      <p className="mt-1 text-sm text-red-600">{errors.title.message}</p>
                    )}
                  </div>

                  {/* Description */}
                  <div>
                    <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                      Solution Description *
                    </label>
                    <textarea
                      {...register('description', {
                        required: 'Solution description is required',
                        minLength: {
                          value: 50,
                          message: 'Description must be at least 50 characters',
                        },
                        maxLength: {
                          value: 2000,
                          message: 'Description must be less than 2000 characters',
                        },
                      })}
                      rows={6}
                      className={`mt-1 block w-full px-3 py-2 border ${
                        errors.description ? 'border-red-300' : 'border-gray-300'
                      } rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm`}
                      placeholder="Describe your approach, methodology, and key insights..."
                    />
                    {errors.description && (
                      <p className="mt-1 text-sm text-red-600">{errors.description.message}</p>
                    )}
                  </div>

                  {/* File Upload */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Solution Files *
                    </label>
                    <div
                      className={`mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-dashed rounded-md transition-colors ${
                        dragActive 
                          ? 'border-primary-400 bg-primary-50' 
                          : errors.files 
                            ? 'border-red-300 bg-red-50' 
                            : 'border-gray-300'
                      }`}
                      onDragEnter={handleDrag}
                      onDragLeave={handleDrag}
                      onDragOver={handleDrag}
                      onDrop={handleDrop}
                    >
                      <div className="space-y-1 text-center">
                        <DocumentArrowUpIcon className="mx-auto h-12 w-12 text-gray-400" />
                        <div className="flex text-sm text-gray-600">
                          <label
                            htmlFor="file-upload"
                            className="relative cursor-pointer bg-white rounded-md font-medium text-primary-600 hover:text-primary-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-primary-500"
                          >
                            <span>Upload files</span>
                            <input
                              {...register('files', {
                                required: 'Please upload at least one file',
                              })}
                              id="file-upload"
                              name="file-upload"
                              type="file"
                              multiple
                              className="sr-only"
                              onChange={handleFileChange}
                              accept=".py,.ipynb,.zip,.tar.gz,.pdf,.md,.txt,.csv,.json"
                            />
                          </label>
                          <p className="pl-1">or drag and drop</p>
                        </div>
                        <p className="text-xs text-gray-500">
                          Python, Jupyter notebooks, ZIP archives, documentation (Max 10MB per file)
                        </p>
                      </div>
                    </div>
                    
                    {/* File List */}
                    {watchedFiles && watchedFiles.length > 0 && (
                      <div className="mt-3">
                        <h4 className="text-sm font-medium text-gray-700 mb-2">Selected Files:</h4>
                        <ul className="space-y-1">
                          {Array.from(watchedFiles).map((file, index) => (
                            <li key={index} className="flex items-center text-sm text-gray-600">
                              <DocumentArrowUpIcon className="h-4 w-4 mr-2 text-gray-400" />
                              <span>{file.name}</span>
                              <span className="ml-2 text-gray-400">
                                ({(file.size / 1024 / 1024).toFixed(2)} MB)
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    {errors.files && (
                      <p className="mt-1 text-sm text-red-600">{errors.files.message}</p>
                    )}
                  </div>

                  {/* Terms Agreement */}
                  <div className="flex items-start">
                    <div className="flex items-center h-5">
                      <input
                        {...register('agreeToTerms', {
                          required: 'You must agree to the terms and conditions',
                        })}
                        id="agree-terms"
                        type="checkbox"
                        className="focus:ring-primary-500 h-4 w-4 text-primary-600 border-gray-300 rounded"
                      />
                    </div>
                    <div className="ml-3 text-sm">
                      <label htmlFor="agree-terms" className="font-medium text-gray-700">
                        I agree to the terms and conditions
                      </label>
                      <p className="text-gray-500">
                        By submitting, you agree that your solution may be evaluated and that results may be made public on leaderboards.
                      </p>
                    </div>
                  </div>
                  {errors.agreeToTerms && (
                    <p className="text-sm text-red-600">{errors.agreeToTerms.message}</p>
                  )}

                  {/* Error Message */}
                  {errors.root && (
                    <div className="rounded-md bg-red-50 p-4">
                      <div className="text-sm text-red-700">{errors.root.message}</div>
                    </div>
                  )}

                  {/* Submit Button */}
                  <div className="flex justify-end space-x-3">
                    <Link
                      to={`/challenges/${challengeId}`}
                      className="btn-outline"
                    >
                      Cancel
                    </Link>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="btn-primary"
                    >
                      {isSubmitting ? (
                        <>
                          <LoadingSpinner size="sm" color="text-white" />
                          <span className="ml-2">Submitting...</span>
                        </>
                      ) : (
                        'Submit Solution'
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>

          {/* Challenge Info Sidebar */}
          <div className="space-y-6">
            {/* Challenge Details */}
            <div className="bg-white shadow rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Challenge Details</h3>
              <dl className="space-y-3">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Title</dt>
                  <dd className="text-sm text-gray-900">{challengeData.title}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Category</dt>
                  <dd className="text-sm text-gray-900">{challengeData.category}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Difficulty</dt>
                  <dd className="text-sm text-gray-900 capitalize">{challengeData.difficulty}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Deadline</dt>
                  <dd className="text-sm text-gray-900">
                    {format(new Date(challengeData.end_date), 'MMM dd, yyyy')}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Max Team Size</dt>
                  <dd className="text-sm text-gray-900">{challengeData.max_team_size}</dd>
                </div>
              </dl>
            </div>

            {/* Submission Guidelines */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <div className="flex">
                <div className="flex-shrink-0">
                  <InformationCircleIcon className="h-5 w-5 text-blue-400" />
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-blue-800">
                    Submission Guidelines
                  </h3>
                  <div className="mt-2 text-sm text-blue-700">
                    <ul className="list-disc list-inside space-y-1">
                      <li>Include all source code and documentation</li>
                      <li>Provide clear instructions to run your solution</li>
                      <li>Include a README file explaining your approach</li>
                      <li>Ensure all files are properly organized</li>
                      <li>Test your solution before submitting</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Evaluation Criteria */}
            {challengeData.evaluation_criteria && challengeData.evaluation_criteria.length > 0 && (
              <div className="bg-white shadow rounded-lg p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Evaluation Criteria</h3>
                <ul className="space-y-2">
                  {challengeData.evaluation_criteria.map((criteria, index) => (
                    <li key={index} className="flex items-start">
                      <span className="flex-shrink-0 w-6 h-6 bg-primary-100 text-primary-600 rounded-full flex items-center justify-center text-sm font-medium mr-3 mt-0.5">
                        {index + 1}
                      </span>
                      <span className="text-sm text-gray-700">{criteria}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}